import { useCallback, useMemo, useState } from 'react'

type MatchLineLike = { sourceRowId: string; targetRowId: string }

type RowSelection = {
  invoiceRowIds: Set<string>
  poRowIds: Set<string>
}

export default function useConnectorSelection() {
  // Click selection comes from clicking a match line in ConnectorCanvas.
  const [clickSelection, setClickSelection] = useState<RowSelection | null>(
    null,
  )
  // Hover selection comes from hovering a row in either LineItemsTable.
  const [hoverSelection, setHoverSelection] = useState<RowSelection | null>(
    null,
  )
  // Discrepancy selection comes from opening a cell's discrepancy popover — it
  // highlights every row in that discrepancy's match group, both sides.
  const [discrepancySelection, setDiscrepancySelection] =
    useState<RowSelection | null>(null)

  const handleConnectorSelectionChange = useCallback(
    (selection: { invoiceRowId: string; poRowIds: string[] } | null) => {
      if (selection) {
        setClickSelection({
          invoiceRowIds: new Set([selection.invoiceRowId]),
          poRowIds: new Set(selection.poRowIds),
        })
      } else {
        setClickSelection(null)
      }
    },
    [],
  )

  // Given the hovered row and the full set of match lines, highlights the
  // entire match group connected to it — walking the lines in both directions
  // until no new rows are found, so one-to-many and many-to-one matches light
  // up fully from either side. Clears on mouse leave (rowId === null) without
  // touching any active click selection.
  const handleRowHover = useCallback(
    (
      side: 'invoice' | 'po',
      rowId: string | null,
      matchLines: MatchLineLike[],
    ) => {
      if (!rowId) {
        setHoverSelection(null)
        return
      }

      const invoiceRowIds = new Set<string>()
      const poRowIds = new Set<string>()
      if (side === 'invoice') {
        invoiceRowIds.add(rowId)
      } else {
        poRowIds.add(rowId)
      }

      // Walk the match lines until no new rows are found,
      // adding them to the sets above. It needs to be a while loop
      // because the first go around may not find all rows in
      // a one-to-many or many-to-one match group, but after
      // adding the new rows, the next go around may find more.
      let grew = true
      const toVerify = new Set<MatchLineLike>(matchLines)
      while (grew) {
        grew = false
        toVerify.forEach((line) => {
          if (
            invoiceRowIds.has(line.sourceRowId) !==
            poRowIds.has(line.targetRowId)
          ) {
            invoiceRowIds.add(line.sourceRowId)
            poRowIds.add(line.targetRowId)
            toVerify.delete(line)
            grew = true
          }
        })
      }

      setHoverSelection({ invoiceRowIds, poRowIds })
    },
    [],
  )

  // Highlights every row of an open discrepancy's match group (both sides), or
  // clears it (group === null). For a one-to-many/many-to-one match this is
  // more than the two rows directly across from each other.
  const handleDiscrepancyHighlight = useCallback(
    (group: { invoiceRowIds: string[]; poRowIds: string[] } | null) => {
      setDiscrepancySelection(
        group
          ? {
              invoiceRowIds: new Set(group.invoiceRowIds),
              poRowIds: new Set(group.poRowIds),
            }
          : null,
      )
    },
    [],
  )

  // Union click, hover, and discrepancy selections so none clobbers another.
  const highlightedInvoiceRowIds = useMemo(() => {
    const merged = new Set<string>()
    clickSelection?.invoiceRowIds.forEach((id) => merged.add(id))
    hoverSelection?.invoiceRowIds.forEach((id) => merged.add(id))
    discrepancySelection?.invoiceRowIds.forEach((id) => merged.add(id))
    return merged
  }, [clickSelection, hoverSelection, discrepancySelection])

  const highlightedPoRowIds = useMemo(() => {
    const merged = new Set<string>()
    clickSelection?.poRowIds.forEach((id) => merged.add(id))
    hoverSelection?.poRowIds.forEach((id) => merged.add(id))
    discrepancySelection?.poRowIds.forEach((id) => merged.add(id))
    return merged
  }, [clickSelection, hoverSelection, discrepancySelection])

  return {
    highlightedInvoiceRowIds,
    highlightedPoRowIds,
    handleConnectorSelectionChange,
    handleRowHover,
    handleDiscrepancyHighlight,
  }
}
